import React, { useCallback, useMemo, useRef, useState } from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import type {
  CellClassRules,
  ColDef,
  FirstDataRenderedEvent,
  GridReadyEvent,
  GridSizeChangedEvent,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core'

import type { SurecoCsvDataType } from '../constants'
import { getColumnLabel, getVisibleColumnKeys } from '../columnLabels'
import { formatHeaderLabel } from '../utils/compareDataHelpers'
import { computeContentWidth, computeHeaderWidth } from '../utils/columnSizing'
import StatusBadge from './StatusBadge'
import styles from '../PlanManagementV3.module.scss'

const PAGE_SIZE = 50
// Tables narrower than this stretch to the grid's width; wider ones size to
// content and scroll horizontally.
const STRETCH_BELOW_PX = 1000
const MIN_COLUMN_WIDTH = 90

type CompareGridRow = {
  id: string
  compareStatus: string
  fieldCompare: Record<string, unknown>
}

export type CompareGridPage<Row> = { items: Row[]; total: number }

export type CompareGridPageRequest = {
  page: number
  limit: number
  /** The sorted column's colId — the API maps it to a backend sortBy. */
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

/**
 * The server-side results grid both compares share: paging, a pinned status
 * column, columns from the first page's fields sized to their content.
 * Each compare supplies how to fetch a page and how to show a field. Key it
 * by everything `fetchPage` depends on — the datasource is set once on mount.
 */
export function CompareResultsGrid<Row extends CompareGridRow>({
  dataType,
  fetchPage,
  fieldPath,
  cellText,
  cellClassRules,
  renderCell,
}: {
  dataType: SurecoCsvDataType
  fetchPage: (request: CompareGridPageRequest) => Promise<CompareGridPage<Row>>
  /** Column field for a key, e.g. `fieldCompare.<key>.csvValue`. */
  fieldPath: (key: string) => string
  /** A cell's display text, used to size its column. */
  cellText: (row: Row, key: string) => string
  cellClassRules: (key: string) => CellClassRules
  renderCell: (row: Row, key: string) => React.ReactNode
}) {
  // Columns come from the first page that has rows.
  const [fieldKeys, setFieldKeys] = useState<string[]>([])
  // The first page's rows, used only to size columns to their content.
  const [sampleRows, setSampleRows] = useState<Row[]>([])
  // Read in onFirstDataRendered, which fires after colDefs is committed, so a
  // ref (not state) avoids a stale closure.
  const shouldStretchRef = useRef(false)

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      const getRows = async (rowParams: IServerSideGetRowsParams) => {
        const startRow = rowParams.request.startRow ?? 0
        const blockSize = (rowParams.request.endRow ?? PAGE_SIZE) - startRow
        const sortModel = rowParams.request.sortModel?.[0]
        try {
          const page = await fetchPage({
            page: Math.floor(startRow / blockSize) + 1,
            limit: blockSize,
            sortBy: sortModel?.colId,
            sortOrder: sortModel?.sort,
          })
          const first = page.items[0]
          if (first) {
            setFieldKeys((prev) =>
              prev.length > 0
                ? prev
                : getVisibleColumnKeys(
                    dataType,
                    Object.keys(first.fieldCompare)
                  )
            )
            setSampleRows((prev) => (prev.length === 0 ? page.items : prev))
          }
          rowParams.success({ rowData: page.items, rowCount: page.total })
        } catch (err) {
          console.error('Failed to load compare rows', err)
          rowParams.fail()
        }
      }
      params.api.updateGridOptions({ serverSideDatasource: { getRows } })
    },
    [dataType, fetchPage]
  )

  // useMemo (not useCallback) so columnDefs is a stable value, not a function.
  const colDefs = useMemo((): ColDef[] => {
    const statusCol: ColDef = {
      headerName: 'Status',
      field: 'compareStatus',
      width: 120,
      pinned: 'left',
      // Without this, sizeColumnsToFit() shrinks the pinned column too, until
      // the badge gets clipped.
      suppressSizeToFit: true,
      cellRenderer: ({ value }: { value: string }) => (
        <StatusBadge status={value} />
      ),
    }

    const computed = fieldKeys.map((key) => {
      const headerName = getColumnLabel(dataType, key) ?? formatHeaderLabel(key)
      const contentWidth = Math.max(
        computeHeaderWidth(headerName),
        computeContentWidth(sampleRows.map((row) => cellText(row, key))),
        MIN_COLUMN_WIDTH
      )
      return { key, headerName, contentWidth }
    })
    const shouldStretch =
      computed.reduce((sum, c) => sum + c.contentWidth, 0) < STRETCH_BELOW_PX
    shouldStretchRef.current = shouldStretch

    const dataCols: ColDef[] = computed.map(
      ({ key, headerName, contentWidth }) => ({
        headerName,
        field: fieldPath(key),
        ...(shouldStretch
          ? { minWidth: contentWidth, flex: 1 }
          : { width: contentWidth }),
        cellClassRules: cellClassRules(key),
        cellRenderer: (p: { data?: Row }) =>
          p.data ? renderCell(p.data, key) : '',
      })
    )

    return [statusCol, ...dataCols]
  }, [
    dataType,
    fieldKeys,
    sampleRows,
    fieldPath,
    cellText,
    cellClassRules,
    renderCell,
  ])

  const onFirstDataRendered = useCallback((params: FirstDataRenderedEvent) => {
    if (shouldStretchRef.current) params.api.sizeColumnsToFit()
    else params.api.autoSizeAllColumns()
  }, [])

  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    if (shouldStretchRef.current) params.api.sizeColumnsToFit()
  }, [])

  return (
    <div className={`ag-theme-alpine ${styles.compareGrid}`}>
      <AgGridReact
        columnDefs={colDefs}
        rowModelType="serverSide"
        cacheBlockSize={PAGE_SIZE}
        pagination
        paginationPageSize={PAGE_SIZE}
        suppressContextMenu
        enableCellTextSelection
        ensureDomOrder
        defaultColDef={{ resizable: true, sortable: true, filter: false }}
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        onGridSizeChanged={onGridSizeChanged}
        getRowId={(p: { data: Row }) => p.data.id}
      />
    </div>
  )
}
